<template>
  <Collapsible
    v-if="filter"
    class="filter__section"
    :isCollapsed="isCollapsed"
    :isCollapsible="isCollapsible"
    :max-height="28"
    :min-height-to-collapse="filter.alwaysExpanded ? 10000 : 65"
    @toggle="onToggle"
  >
    <template #header>
      <div class="filter__section-header">
        <h3>{{ filter.title }}</h3>
        <UpDownArrow
          class="filter__section-icon"
          :type="isCollapsed ? 'down' : 'up'"
        />
        <div class="filter__section-selected-values">
          {{ selectedFilter?.join(',') }}
        </div>
      </div>
    </template>
    <template #body>
      <div class="filter__options">
        <sib-tag
          size="sm"
          round="sm"
          :role="isSelected(bucket.value) ? 'primary' : 'default'"
          class="filter__option"
          v-for="(bucket, index) in isShowSeeMore
            ? showFirstItems
            : filter.buckets"
          :key="bucket.value + index"
          @click="onTagClick(bucket.value)"
          v-show="bucket.count > 0"
        >
          {{ bucket.value }}
        </sib-tag>
        <sib-button
          size="sm"
          role="tertiary"
          v-if="isShowSeeMore"
          @click="onSeeMoreClick"
        >
          Show More
        </sib-button>
      </div>
    </template>
  </Collapsible>
</template>

<script lang="ts" setup>
import Collapsible from '@/ui-kits/Collapsible.vue'
import UpDownArrow from '@/ui-kits/UpDownArrow.vue'
import { FilterSection } from '@/types'
import { onMounted, ref, watch } from 'vue'
import { computed } from '@vue/reactivity'
import SibTag from '@/ui-kits/Tag.vue'
import useDeviceStore from '@/store/device'

interface FilterSectionProps {
  filter: FilterSection | null
  selectedFilter: string[]
  isCollapsible?: boolean
}

const props = withDefaults(defineProps<FilterSectionProps>(), {
  filter: null,
  selectedFilter: () => [],
  isCollapsible: true,
})

const emit = defineEmits<{
  (e: 'toggle-section', value: boolean): void
  (e: 'toggle-filter', value: string): void
}>()

const deviceStore = useDeviceStore()

const isCollapsed = ref(false)
const isShowSeeMore = ref(true)
const isSeeMoreClicked = ref(false)
const showMoreNumberOfItemsThreshold = deviceStore.isMobile ? 20 : 40
const showFirstItems = computed(() => {
  return props.filter?.buckets.slice(0, showMoreNumberOfItemsThreshold) || []
})

onMounted(() => {
  watch(
    () => props.filter?.buckets.length,
    () => {
      if (
        !props.filter?.alwaysExpanded &&
        props.filter?.buckets.length &&
        props.filter?.buckets.length > showMoreNumberOfItemsThreshold
      ) {
        // only show see more for the first time it is not clicked
        if (!isSeeMoreClicked.value) {
          isShowSeeMore.value = true
        }
      } else {
        isShowSeeMore.value = false
      }
    },
    { immediate: true }
  )
})

function onSeeMoreClick() {
  isShowSeeMore.value = false
  isSeeMoreClicked.value = true
}

function onToggle(value: boolean) {
  isCollapsed.value = value
}

function onTagClick(value: string) {
  emit('toggle-filter', value)
}

function isSelected(value: string) {
  return props.selectedFilter?.includes(value)
}
</script>

<style lang="scss">
.filter {
  &__section {
    margin-top: 1em;

    &.collapsible--always-expanded .filter__section-icon {
      display: none;
    }
  }

  &__section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: solid 1px var(--border-color);

    h3 {
      font-size: 1em;
      margin: 0;
      line-height: 26px;
      white-space: nowrap;
    }

    .sib-arrow {
      min-width: var(--sib-arrow-width);
      margin-left: 10px;
    }
  }

  &__section-selected-values {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0 1em;
    font-size: 0.875rem;
    text-align: right;
  }

  &__options {
    display: flex;
    flex-wrap: wrap;
    gap: 0.8em;
    margin-top: 0.5em;

    .sib-tag {
      cursor: pointer;
    }

    .sib-tag--size-sm {
      font-size: 0.875rem;
    }

    .sib-button--tertiary {
      width: auto;
      font-size: 0.875rem;
      padding: 0.25rem 0.875rem;
      height: auto;
      border: solid 1px var(--border-color);
    }
  }
}
</style>
